/**
 * 静岡県 名物めし 12段階（コンテンツデータ）。
 *
 * 数値（radius / mass / score / unlockScore）は福岡・北海道・山形と同じテンプレート。
 * 進行の軸は「物理的な大きさ ＝ ごちそう度」で、県ごとに触らない。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 桜えびを「駿河湾でしか獲れない」と断定しない。漁の対象が駿河湾だけ、が正確。
 *  - 安倍川もちの「家康が命名した」を断定しない（二説あり）。
 *  - うなぎは「発祥の地」ではなく「うなぎ養殖発祥の地」。
 *  - 浜松餃子を「餃子消費量日本一」と断定しない。家計調査の順位は年で入れ替わる。
 *  - 富士宮やきそばの「B-1グランプリ優勝」は公式で裏が取れなかったので書かない。
 *  - わさびは「わさび栽培の起源が有東木」であって、わさび自体の発祥ではない。
 *  - 店名は表示名に使わない。
 *
 * 本番画像は public/assets/foods/shizuoka/ に置く。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：わさび漬け（店で食べられる品に差し替え）
 * - 三島コロッケ：B-1グランプリでの入賞（平成21年・8位）は順位の話で料理の説明ではないので書かない
 * - 三島コロッケ：三島馬鈴薯が「日本でもトップクラスの値段で取引」は観光協会の記述のみで、価格にかかわる情報なので書かない
 * - 三島コロッケ：姉妹品（甘藷みしまコロッケ・里芋みしまコロッケ）は品が別なので書かない
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';

import { SHIZUOKA_FOOD_HULLS } from './shizuokaFoodShapes.generated';
import { shizuokaSceneryTour } from './shizuokaScenes';

/** 「食べに行く」導線の既定リンク（特定店の宣伝にならないよう検索リンクにする）。 */
function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'mishima-croquette',
    level: 1,
    name: '三島コロッケ',
    shortName: '三島コロッケ',
    image: '/assets/foods/shizuoka/01_mishima_croquette.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '箱根西麓でとれた三島馬鈴薯を100%使ったコロッケ。外はさくっと、中はクリーミーで甘みが強い。具や形、味付けは店ごとに任され、仕入先を確かめた認定店だけが販売できる。',
    source:
      'https://www.city.mishima.shizuoka.jp/mishima_info/croquette/',
    icon: { label: 'コロッケ', shape: 'mochi', baseColor: '#c98a2e', accentColor: '#f3e2a6' },
    reward: {
      unlockScore: 200,
      area: '三島市',
      headline: '三島の馬鈴薯だけで作る',
      tip: '三島馬鈴薯の出荷は7月ごろの約1か月だけ。手で掘って収穫し、天日に干してから涼しい倉庫で熟成させる。みしまコロッケは平成20年7月に生まれた。',
      links: searchLinks('三島市', 'みしまコロッケ'),
      official: [{ label: '三島市「みしまコロッケ」', url: 'https://www.city.mishima.shizuoka.jp/mishima_info/croquette/' }, { label: 'しずおか食の情報センター（静岡県）「みしまコロッケ」', url: 'https://fujinokuni.shokunomiyako-shizuoka.pref.shizuoka.jp/culture/article/1786' }],
    },
  },
  {
    id: 'abekawa-mochi',
    level: 2,
    name: '安倍川もち',
    shortName: '安倍川もち',
    image: '/assets/foods/shizuoka/02_abekawa_mochi.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: 'つきたての餅に、砂糖入りのきな粉。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/36_18_shizuoka.html',
    icon: { label: '安倍川', shape: 'mochi', baseColor: '#d8b878', accentColor: '#f4efe4' },
    reward: {
      unlockScore: 500,
      area: '静岡市',
      headline: 'つきたての餅に、砂糖を混ぜたきな粉をまぶす。いまは小豆餡で包んだものも並ぶ。',
      tip: '名前の由来は、徳川家康が名づけたという説と、東海道の安倍川の茶屋の名物だったという説の二つ。『東海道中膝栗毛』にも出てくる。',
      links: searchLinks('静岡市', '安倍川もち'),
    },
  },
  {
    id: 'kuro-hanpen',
    level: 3,
    name: '黒はんぺん',
    shortName: '黒はんぺん',
    image: '/assets/foods/shizuoka/03_kuro_hanpen.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '骨も皮も、丸ごと練り込む。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/36_1_shizuoka.html',
    icon: { label: '黒はんぺん', shape: 'mochi', baseColor: '#9aa0a0', accentColor: '#5b6363' },
    reward: {
      unlockScore: 1000,
      area: '焼津',
      headline: '鯖・鯵・鰯などをすり身にして茹でたもの。魚の骨や皮を丸ごと練り込むので灰色になる。',
      tip: '前の浜でたくさん獲れた青魚を加工して日持ちさせたのが始まり。もとは「はんべ」と呼ばれ、白いものと区別するために「黒はんぺん」になった。形は半月形。',
      links: searchLinks('焼津市', '黒はんぺん'),
    },
  },
  {
    id: 'shizuoka-oden',
    level: 4,
    name: '静岡おでん',
    shortName: '静岡おでん',
    image: '/assets/foods/shizuoka/04_shizuoka_oden.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '黒いつゆ、串、青海苔とだし粉。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/36_16_shizuoka.html',
    icon: { label: 'おでん', shape: 'pot', baseColor: '#3f2f24', accentColor: '#d8cbb0' },
    reward: {
      unlockScore: 1800,
      area: '静岡市',
      headline: '静岡市が挙げる「静岡おでんの五ヶ条」は、黒いスープ・串に刺してある・黒はんぺんが入っている・青海苔とだし粉をかける・駄菓子屋にもある。',
      tip: '始まりは大正時代。戦後、捨てられていた牛すじや豚モツを具にしたところ人気が出た。駄菓子屋や惣菜屋で1本から買える。',
      links: searchLinks('静岡市', '静岡おでん'),
    },
  },
  {
    id: 'nama-shirasu',
    level: 5,
    name: '生しらす',
    shortName: 'しらす',
    image: '/assets/foods/shizuoka/05_nama_shirasu.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '生後1〜2か月の、透きとおった稚魚。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/36_23_shizuoka.html',
    icon: { label: 'しらす', shape: 'roe', baseColor: '#e8e4d8', accentColor: '#8fa9c4' },
    reward: {
      unlockScore: 3000,
      area: '用宗・由比・舞阪',
      headline: 'カタクチイワシやマイワシの、生後1〜2か月の稚魚。駿河湾はプランクトンが多い日本有数の漁場。',
      tip: '船曳網漁で3月末から1月中旬まで獲る。静岡県のしらすは全国で一、二を争う漁獲量で、二艘曳きが主流。',
      links: searchLinks('静岡市', '生しらす'),
    },
  },
  {
    id: 'sakura-ebi',
    level: 6,
    name: '桜えび',
    shortName: '桜えび',
    image: '/assets/foods/shizuoka/06_sakura_ebi.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '夜のあいだだけ、海面近くへ上がってくる。',
    source: 'https://www.city.shizuoka.lg.jp/s2473/s005021.html',
    icon: { label: '桜えび', shape: 'roe', baseColor: '#f08a7a', accentColor: '#d9534f' },
    reward: {
      unlockScore: 4500,
      area: '由比・大井川',
      headline: '静岡市の公式サイトは「他の海域にも生息してはいますが、漁獲対象となっている場所は、日本では駿河湾しかありません」と記している。',
      tip: '漁期は春が3月下旬から6月上旬、秋が10月下旬から12月下旬。産卵期の6〜8月は禁漁。水深400〜600メートルにいて、夜に浮いてきた分だけを獲る。',
      links: searchLinks('静岡市', '桜えび'),
    },
  },
  {
    id: 'fujinomiya-yakisoba',
    level: 7,
    name: '富士宮やきそば',
    shortName: '富士宮やきそば',
    image: '/assets/foods/shizuoka/07_fujinomiya_yakisoba.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '肉かすと、いわしの削り粉。',
    source: 'https://www.city.fujinomiya.lg.jp/1025110000/p001736.html',
    icon: { label: 'やきそば', shape: 'noodle-bowl', baseColor: '#b5763f', accentColor: '#4d6b3a' },
    reward: {
      unlockScore: 6500,
      area: '富士宮',
      headline: '肉かすとイワシの削り粉がかかっているのが特徴。麺は蒸したあと強制的に冷やし、油で表面をコーティングするのでコシが強い。',
      tip: '冷蔵庫が普及していない時代、市内にたくさんあったやきそば店の求めに応えて、日持ちする麺の作り方が確立した。令和3年度に文化庁の「100年フード」に認定。',
      links: searchLinks('富士宮市', '富士宮やきそば'),
    },
  },
  {
    id: 'hamamatsu-gyoza',
    level: 8,
    name: '浜松餃子',
    shortName: '浜松餃子',
    image: '/assets/foods/shizuoka/08_hamamatsu_gyoza.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '円く並べて、真ん中にもやし。',
    source: 'https://www.city.hamamatsu.shizuoka.jp/documents/70476/16food.pdf',
    icon: { label: '餃子', shape: 'dumpling', baseColor: '#e2c48a', accentColor: '#8a5a32' },
    reward: {
      unlockScore: 9000,
      area: '浜松',
      headline: 'キャベツを中心に野菜をたっぷり練り込み、皮が薄くてあっさりしている。',
      tip: '円く並べるのは、丸いフライパンを目いっぱい使うための形。真ん中の空いたところに何か添えようと試行錯誤してたどりついたのが、もやしだった。',
      links: searchLinks('浜松市', '浜松餃子'),
    },
  },
  {
    id: 'sakuraebi-kakiage-don',
    level: 9,
    name: '桜えびのかき揚げ丼',
    shortName: 'かき揚げ丼',
    image: '/assets/foods/shizuoka/09_sakuraebi_kakiage_don.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '揚げると、桜色がいっそう濃くなる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/36_28_shizuoka.html',
    icon: { label: 'かき揚げ', shape: 'rice-bowl', baseColor: '#e0834f', accentColor: '#f3e6c6' },
    reward: {
      unlockScore: 12000,
      area: '由比・静岡市',
      headline: '生の桜えびに三つ葉かねぎを混ぜ、180度の油で揚げる。家庭でもよく作られる静岡の定番。',
      tip: 'そのままでも、丼にしても、蕎麦やうどんの具にしてもいい。由比港漁協は毎年5月に「由比桜えびまつり」を開いている。',
      links: searchLinks('静岡市', '桜えび かき揚げ丼'),
    },
  },
  {
    id: 'kinmedai-nitsuke',
    level: 10,
    name: '金目鯛の煮付け',
    shortName: '金目鯛',
    image: '/assets/foods/shizuoka/10_kinmedai_nitsuke.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '醤油と酒と砂糖で、甘く煮る。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/36_17_shizuoka.html',
    icon: { label: '金目鯛', shape: 'fish', baseColor: '#c1392b', accentColor: '#f0c078' },
    reward: {
      unlockScore: 16000,
      area: '下田・稲取',
      headline: '農林水産省は「静岡県伊豆半島は金目鯛の産地であり、下田港は金目鯛の水揚げが日本一の漁港」と記している。',
      tip: '伊豆で獲れる金目鯛は3種あり、一本釣りの地金目鯛（稲取キンメ）が最上級とされる。ただし水揚げが激減していて、手に入りにくい。',
      links: searchLinks('下田市', '金目鯛の煮付け'),
    },
  },
  {
    id: 'unagi-kabayaki',
    level: 11,
    name: 'うなぎの蒲焼き',
    shortName: 'うなぎ',
    image: '/assets/foods/shizuoka/11_unagi_kabayaki.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '浜名湖のほとりで、100年以上。',
    source: 'https://www.city.hamamatsu.shizuoka.jp/miryoku/hakken/tokusan/unagi.html',
    icon: { label: 'うなぎ', shape: 'rice-bowl', baseColor: '#5c3418', accentColor: '#f3e6c6' },
    reward: {
      unlockScore: 22000,
      area: '浜松・浜名湖',
      headline: '明治33年、服部倉治郎が浜名湖を養殖に最も向いた場所と見て、舞阪に養鰻池を作った。浜松市は「うなぎ養殖発祥の地」としている。',
      tip: '三方原台地の豊富な地下水、浜名湖にのぼる稚魚、後背地からの飼料、年平均15℃前後の気候がそろった。関東風は背開きにして蒸し、関西風は腹開きで蒸さずに焼く。',
      links: searchLinks('浜松市', 'うなぎ'),
    },
  },
  {
    id: 'enshunada-torafugu',
    level: 12,
    name: '遠州灘の天然とらふぐ',
    shortName: 'とらふぐ',
    image: '/assets/foods/shizuoka/12_enshunada_torafugu.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '延縄で、一尾ずつ釣り上げる。',
    source: 'https://fujinokuni.shokunomiyako-shizuoka.pref.shizuoka.jp/buy-food/1954',
    icon: { label: 'とらふぐ', shape: 'sashimi-platter', baseColor: '#f0e6d8', accentColor: '#3f4a52' },
    reward: {
      unlockScore: 30000,
      area: '浜松・遠州灘',
      headline: '静岡県は「遠州灘は日本一の天然とらふぐの漁場」としている。延縄漁で一尾ずつ釣り上げ、活かしたまま運ぶ。旬は10月から2月。',
      tip: '遠州灘でのフグ漁が始まったのは昭和60年代。平成15年に地元へ加工処理工場ができて「遠州灘天然とらふぐ」として売り出された。いま国内に流通するトラフグの9割は養殖で、天然は1割ほど。',
      links: searchLinks('浜松市', '遠州灘 天然とらふぐ'),
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const shizuokaFoodStage: StageDefinition = {
  regionId: 'shizuoka',
  regionName: '静岡県',
  mode: 'food',
  title: '静岡めしマージ',
  theme: {
    primaryColor: '#3a7a8c',
    secondaryColor: '#e0a13c',
    accentColor: '#f2d16b',
    backgroundColor: '#161c1f',
  },
  /** 背景ツアー「静岡めぐり」。軸は季節。5月から翌4月へ一周する。 */
  scenery: {
    tour: shizuokaSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, SHIZUOKA_FOOD_HULLS),
};
